/**
 * Shared response/transport types that mirror the backend's standard envelopes
 * (see factory-backend/src/utils/ApiResponse.ts and error.middleware.ts).
 */

/** Pagination metadata returned alongside list endpoints. */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/** Success envelope: every endpoint returns `{ success, message, data, meta? }`. */
export interface ApiEnvelope<T> {
  success: true;
  message: string;
  data: T;
  meta?: PaginationMeta;
}

/** Error envelope produced by the backend error middleware. */
export interface ApiErrorBody {
  success: false;
  message: string;
  /** Field-level validation errors (`{ field: string[] }`) from Zod, when present. */
  details?: Record<string, string[]> | unknown;
  stack?: string;
}

/** A list response paired with its pagination metadata. */
export interface Paginated<T> {
  items: T[];
  meta: PaginationMeta;
}

/** Common list query parameters accepted by paginated endpoints. */
export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Normalised error thrown by the API client. Carries the HTTP status, a
 * human-readable message and any field-level validation details so UI code
 * can surface them without re-parsing Axios internals.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly details?: Record<string, string[]> | unknown;
  readonly isNetworkError: boolean;

  constructor(
    message: string,
    status: number,
    details?: Record<string, string[]> | unknown,
    isNetworkError = false,
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.details = details;
    this.isNetworkError = isNetworkError;
  }

  /** Flatten Zod-style field errors into a single readable string. */
  get fieldErrorText(): string | undefined {
    if (!this.details || typeof this.details !== "object") return undefined;
    const entries = Object.entries(this.details as Record<string, string[]>);
    if (entries.length === 0) return undefined;
    return entries
      .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(", ")}`)
      .join(" • ");
  }
}
